import { type AuthorCMS } from "@repo/common/src/types/firestore/cms/author";
import { CMSCollections } from "@repo/common/src/types/firestore/collections";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import { buildCollection, buildEntityCallbacks, buildProperty, type EntityOnSaveProps } from "@firecms/core";
import { getFirestore } from "firebase/firestore";

import { generateUniqueSlug } from "../utils/generateUniqueSlug";
import { Groups } from "./types";
import { standardPermissions } from "../utils/constants";
import { loadingOverlay } from "../utils/loadOverlay";

const callbacks = buildEntityCallbacks({
  onPreSave: async (props: EntityOnSaveProps<AuthorCMS>) => {
    const { values, entityId } = props;
    loadingOverlay.show();

    if (values.name) {
      const baseSlug = values.slug || values.name;
      values.slug = await generateUniqueSlug(getFirestore(), CMSCollections.Authors, baseSlug, entityId);
    }

    return values;
  },
  onSaveSuccess: async () => {
    loadingOverlay.hide();
  },
  onSaveFailure: async () => {
    loadingOverlay.hide();
  },
});

export const authors = buildCollection<AuthorCMS>({
  id: CMSCollections.Authors,
  name: "Autoren",
  singularName: "Autor",
  path: CMSCollections.Authors,
  icon: "People",
  group: Groups.Contributor,
  permissions: () => {
    return standardPermissions;
  },
  subcollections: [],
  callbacks,
  properties: {
    name: {
      name: "Name",
      validation: { required: true },
      dataType: "string",
    },
    slug: {
      name: "URL-freundlicher Slug. Wird automatisch generiert, wenn leer.",
      dataType: "string",
    },
    description: {
      name: "Beschreibung",
      multiline: true,
      dataType: "string",
      columnWidth: 300,
      validation: { required: true },
    },
    image: buildProperty({
      name: "Image",
      description: "Bild des Autors. Bitte in 512x512 hochladen.",
      dataType: "string",
      validation: { required: true },
      storage: {
        storagePath: StoragePaths.AuthorsImages,
        acceptedFiles: ["image/*"],
        maxSize: 1 * 1024 * 1024, // 1 MB in bytes
        imageCompression: {
          maxHeight: 512,
          maxWidth: 512,
        },
      },
    }),
    imageClosing: buildProperty({
      name: "Abschluss Image",
      description: "Bild des Autors, dass zum Ende einer Lektion angezeigt wird. Bitte in 512x512 hochladen.",
      dataType: "string",
      validation: { required: true },
      storage: {
        storagePath: StoragePaths.AuthorsImages,
        acceptedFiles: ["image/*"],
        maxSize: 1 * 1024 * 1024, // 1 MB in bytes
        imageCompression: {
          maxHeight: 512,
          maxWidth: 512,
        },
      },
    }),
    instagram: {
      name: "Instagram handle",
      dataType: "string",
      validation: { required: false },
      description:
        "Instagram handle des Autors. Bitte nicht die ganze URL eintragen, sondern nur den handle. z.B. @maxmustermann",
    },
    website: {
      name: "Website",
      dataType: "string",
      url: true,
      validation: { required: false },
      description: "Website des Autors",
    },
    created_on: buildProperty({
      name: "Erstellungsdatum",
      dataType: "date",
      autoValue: "on_create",
    }),
    updated_on: buildProperty({
      name: "Aktualisierungsdatum",
      dataType: "date",
      autoValue: "on_update",
    }),
  },
});
